import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first, take, takeUntil } from 'rxjs';
import Constants from 'src/app/constants.json';
import { StudyFile } from 'src/app/models/Study/study-file.model';
import { SaleHorseSetting } from 'src/app/models/sales/sale-horse-settings.model';
import { ActivityLogService } from 'src/app/services/activity-log.service';
import { SalesService } from 'src/app/services/sales.service';
import { ViewerService } from 'src/app/services/viewer.service';
import { BaseUnsubscribeComponent } from 'src/app/shared-new/components/base-unsubscribe/base-unsubscribe.component';
import { StudyMimeType } from 'src/app/shared-new/enums/study-mime-type.enum';
import { ViewMode } from 'src/app/shared-new/enums/view-mode.emun';
import { ToastService } from 'src/app/shared-new/services/toast.service';
import { SalesFacade } from 'src/app/shared-new/store/sales/sales.facade';
import { StudiesFacade } from 'src/app/shared-new/store/studies/studies.facade';
import { environment } from 'src/environments/environment';
import swal from 'sweetalert2';
import { SaleHorseStudyBase } from '../../../../models/Study/study.model';
import { AuthService } from '../../../../services/auth.service';
import { DateParserFormatter } from '../../../../utils/helper/date-parser-formatter';
import { DocumentTypeModalComponent } from '../../../edit-clinical-study-profile/components/document-type-modal/document-type-modal.component';
import { DownloadUtilsService } from 'src/app/services/download-utils-service';
import { DiscloseClientPopupComponent } from './disclose-client-popup/disclose-client-popup.component';

@Component({
  selector: 'app-approved-study',
  templateUrl: './approved-study.component.html',
  styleUrls: ['./approved-study.component.scss'],
})
export class ApprovedStudyComponent
  extends BaseUnsubscribeComponent
  implements OnInit {
  @Input() isViewerOnly: boolean = false;
  @Input() isViewXraysAllowed: boolean = false;
  @Input() viewXrayValidationMessage: string = '';
  @Input() saleSettings: SaleHorseSetting;
  @Input() disclosedClient: string;    
  @Output() disclosedClientChanged = new EventEmitter();
  mp4StudyFiles: StudyFile[] = [];
  pdfStudyFiles: StudyFile[] = [];
  xrayStudyFiles: StudyFile[] = [];
  matchedStudyFiles: StudyFile[] = [];
  additionalStudyFiles: StudyFile[] = [];
  statuses$ = this.studyFacade.statuses$;
  ViewMode = ViewMode;
  study: SaleHorseStudyBase;

  mediaCategories$ = this.studyFacade.mediaCategories$;
  studyFileDocTypeUpdated = this.studyFacade.studyFileDocTypesUpdated$;


  deniedStudyFileIds: number[] = [];

  studyFilesByTypeAndStatusMap$ =
    this.studyFacade.approvedStudyFilesByTypeAndStatusMap$;

  activeSaleHorseStudy$ = this.studyFacade.activeSaleHorseStudy$;
  xrayReportPdfFiles$ = this.studyFacade.xrayReportPdfFiles$;
  public scopeForms: { studyFileId: number; scopeForm: FormGroup }[] = [];

  viewerForm = new FormGroup({
    clientName: new FormControl('', [Validators.required]),
    viewedXray: new FormControl(false),
  });
  activityLogId: number = 0;
  isActivtyEnded: boolean = true;

  scopeDatasource: any[] = [];
  reportDatasource: any[] = [];
  xrayDatasource: any[] = [];
  dateFormat: string = '';
  loggedInUserId: number = 0;
  loggedInMemberId: number = 0;
  isInitialLoad = true; // Flag for initial load
  isShowDisclosedClient: boolean = false;
  public baseDicomUrl = environment.dicomViewer;

  constructor(
    private studyFacade: StudiesFacade,
    private modalService: NgbModal,
    private domSanitizer: DomSanitizer,
    private viewerService: ViewerService,
    private saleService: SalesService,
    private dateParserFormatter: DateParserFormatter,
    private authService: AuthService,
    private changeRef: ChangeDetectorRef,
    private activityLogService: ActivityLogService,
    private saleFacade: SalesFacade,
    private toastService: ToastService,
    private downloadUtilsService: DownloadUtilsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.attachEvents();
  }

  private attachEvents() {   
    this.studyFileDocTypeUpdated
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        if (response) {
          this.studyFacade.getSaleHorseStudies(
            this.study.saleId,
            this.study.horseId
          );
        }
      });
    this.authService
      .getUserData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        const user = JSON.parse(response);
        this.loggedInUserId = user.userId;
        this.loggedInMemberId = user.memberId;
      });
    this.activeSaleHorseStudy$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((study) => {
        this.study = study;
        
        this.dateFormat =
          this.dateParserFormatter.getDefaultDateTimeFormat(true);

        this.saleService
          .getSaleSettings(study.saleId)
          .pipe(take(1))
          .subscribe((saleSettings) => {
            this.studyFilesByTypeAndStatusMap$
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((map) => {
                const scopeVideoDatasource = map.mp4StudyFiles.map(
                  (studyFile) =>
                    this.createDatasourceModel(
                      studyFile,
                      [studyFile.studyFileId],
                      saleSettings.scopeVideo
                    )
                );
                this.scopeDatasource = [...scopeVideoDatasource];

                const xrayDatasource = map.xrayStudyFileItems.map((model) =>
                  this.createDatasourceModel(
                    model.studyFile,
                    model.studyFileIds,
                    saleSettings.xRays
                  )
                );

                this.xrayDatasource = [...xrayDatasource];

                const reportDatasource = map.pdfStudyFiles
                  .filter(
                    (sf) => sf.studyFileMimeTypeId === StudyMimeType.XRAY_REPORT
                  )
                  .map((studyFile) =>
                    this.createDatasourceModel(
                      studyFile,
                      [studyFile.studyFileId],
                      saleSettings.pdfReports
                    )
                  );

                this.reportDatasource = [...reportDatasource];

                map.mp4StudyFiles.forEach((item) => {
                  const studyFileId = item.studyFileId;
                  let formGroup = null;
                  if (
                    saleSettings.scopeVideo &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', [Validators.required]),
                    });
                  } else if (
                    !saleSettings.scopeVideo &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', []),
                    });
                  }
                  this.scopeForms.push({
                    studyFileId: studyFileId,
                    scopeForm: formGroup,
                  });
                });

                map.pdfStudyFiles.forEach((item) => {
                  const studyFileId = item.studyFileId;
                  let formGroup = null;
                  if (
                    saleSettings.pdfReports &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', [Validators.required]),
                    });
                  } else if (
                    !saleSettings.pdfReports &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', []),
                    });
                  }
                  this.scopeForms.push({
                    studyFileId: studyFileId,
                    scopeForm: formGroup,
                  });
                });

                map.xrayStudyFiles.forEach((item) => {
                  const studyFileId = item.studyFileId;
                  let formGroup = null;
                  if (
                    saleSettings.xRays &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', [Validators.required]),
                    });
                  } else if (
                    !saleSettings.xRays &&
                    !this.authService.IsSalesCompany()
                  ) {
                    formGroup = new FormGroup({
                      clientName: new FormControl('', []),
                    });
                  }
                  this.scopeForms.push({
                    studyFileId: studyFileId,
                    scopeForm: formGroup,
                  });
                });

                this.mp4StudyFiles = map.mp4StudyFiles;

                this.pdfStudyFiles = map.pdfStudyFiles.filter(
                  (sf) => sf.studyFileMimeTypeId === StudyMimeType.XRAY_REPORT
                );

                this.xrayStudyFiles = map.xrayStudyFiles;

                this.matchedStudyFiles = this.xrayStudyFiles.filter(
                  (i) => !i.isAdditional
                );
                this.additionalStudyFiles = this.xrayStudyFiles.filter(
                  (i) => i.isAdditional
                );
                if (!this.authService.IsSalesCompany() && (this.saleSettings.pdfReports || this.saleSettings.scopeVideo || this.saleSettings.xRays)) {
                  this.isShowDisclosedClient = true;
                  if (this.isInitialLoad && (this.disclosedClient === null || this.disclosedClient === '')) {
                        this.openDiscloseClientPopup();
                    }
                    this.isInitialLoad = false;
                }
                window.setTimeout(this.detectChangesAsync, 100);
              });
          });
      });
  }
  
  public openDiscloseClientPopup(): void {
    
    const modalRef = this.modalService.open(DiscloseClientPopupComponent, {
      size: '35-width',
      centered: true,
      keyboard: false,
      backdrop: 'static',
    });    

    modalRef.componentInstance.data = {
      saleId: this.study.saleId,
      horseId: this.study.horseId,
      saleHorse: this.study.saleHorse,
      clientName: this.disclosedClient
    };
    modalRef.componentInstance.onSave.subscribe((result: any) => {
    const viewerName = typeof result === 'string' ? result : result.viewerName;

    this.disclosedClientChanged.emit(viewerName);

    modalRef.close();
    });
  }
  
  // using arrow style syntax because this method is being called in window's context (timeout)
  detectChangesAsync = () => {
    this.changeRef.detectChanges();
  };

  public getScopeForm(studyFileId: number): any {
    const formGroup = this.scopeForms.filter(
      (x) => x.studyFileId === studyFileId
    )[0];
    return formGroup?.scopeForm;
  }
  openVideoModal(studyFile: StudyFile, scopeForm: FormGroup): void {
    this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
      const form = scopeForm?.value;
      this.viewerService.playVideo(study, studyFile, this.disclosedClient);
      this.getSaleMemberAccessData();
    });
  }
  openDocTypePopup(studyFile: StudyFile, isReport: boolean): void {
    
    const modalRef = this.modalService.open(DocumentTypeModalComponent, {
      ariaLabelledBy: 'templatePopup',
      centered: true,
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
    });

    modalRef.componentInstance.data = {
      studyFileId: studyFile.studyFileId,
      docTypeId: studyFile.documentTypeId,
      isReport: isReport
    };
    modalRef.result.then(
      (result) => {
        const data = JSON.parse(result);
        this.studyFacade.updateStudyDocType(
          data.studyFileId,
          data.docTypeId,
          data.docTypeName,
          isReport
        );
      },
      (reason) => { }
    );
  }
  openVideoModal2(data: { studyFile: any; form: FormGroup }): void {
    this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
      const studyFile = this.mp4StudyFiles.find(
        (sf) => sf.studyFileId === data.studyFile.studyFileId
      );
      const form = data.form?.value;
      this.viewerService.playVideo(study, studyFile, this.disclosedClient);
      this.getSaleMemberAccessData();
    });
  }

  openPdf(studyFile: StudyFile, scopeForm: FormGroup): void {
    this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
      const form = scopeForm?.value;
      this.viewerService.openPdf(study, studyFile, this.disclosedClient);
      this.getSaleMemberAccessData();
    });
  }

  openPdf2(data: { studyFile: any; form: FormGroup }): void {    
    this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
      const studyFile = this.pdfStudyFiles.find(
        (sf) => sf.studyFileId === data.studyFile.studyFileId
      );
      const form = data.form?.value;
      this.viewerService.openPdf(study, studyFile, this.disclosedClient);
      this.getSaleMemberAccessData();
    });
  }
  viewPdf(studyFile: StudyFile): void {
    swal
      .fire({
        title: 'X-Ray Images',
        text: 'Enter Client Name',
        input: 'text',
        showCancelButton: true,
      })
      .then(async (result) => {
        if (result.value) {
          this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
            this.viewerService.openPdf(study, studyFile);
            this.getSaleMemberAccessData();
          });
        }
      });
  }

  openXrayDownloaderPopup(studyFileIds: number[]): void {
    const files = this.xrayStudyFiles.filter((sf) =>
      studyFileIds.includes(sf.studyFileId)
    );
    this.downloadUtilsService.triggerDownloaderPopup(null, files, [], [], 'submissions');
  }

  openVideoDownloader(studyFileIds: number[]): void {
    const file = this.mp4StudyFiles.filter(x => studyFileIds.includes(x.studyFileId));
    if (file.length > 0) {
      this.downloadUtilsService.triggerFileDownload(file[0].thumbURL, file[0].originalFileName);
    }
  }

  openDocDownloader(studyFileIds: number[]): void {    
    const file = this.pdfStudyFiles.filter(x => studyFileIds.includes(x.studyFileId));
    if (file.length > 0) {
      this.downloadUtilsService.triggerFileDownload(file[0].thumbURL, file[0].originalFileName);
    }
  }

  DeleteFile(studyFile: StudyFile) {
    swal
      .fire({
        title: Constants.messages.deleteStudyFile.title,
        text: Constants.messages.deleteStudyFile.messageText,
        showCancelButton: true,
        cancelButtonText: Constants.messages.cancelButtonText,
        cancelButtonColor: '#6c757d',
        confirmButtonText: Constants.messages.deleteStudyFile.confirmButtonText,
        confirmButtonColor: '#1b6da6',
      })
      .then((result) => {
        if (result.value) {
          this.studyFacade.deleteStudyFile(studyFile.studyFileId);
        }
      });
  }
  openViewer(data: { form: FormGroup; studyFile: any }): void {
    if (!this.isViewXraysAllowed) {
      this.toastService.error(
        'You cannot view the images until you view the reports'
      );
      return;
    }
    this.activeSaleHorseStudy$.pipe(take(1)).subscribe((study) => {
      const form = data.form?.value;

      this.viewerService.openDicomViewerForStudy(
        study,
        null,
        this.disclosedClient,
        data.studyFile.studyFileIds
      );

      this.getSaleMemberAccessData();
      /*this.startActivity(Activity.XRayView, null, null, study);
      const modalRef = this.modalService.open(StudyPreviewModalComponent, {
        centered: true,
        keyboard: false,
        size: 'full-width',
        backdrop: 'static',
      });
      modalRef.componentInstance.url = `${this.baseDicomUrl}/${study?.studyInstanceUID}`;
      modalRef.componentInstance.horseName = study?.saleHorse.name;
      modalRef.componentInstance.study = study;
      modalRef.result.then(
        (result) => {
          this.endActivity();
        },
        (reason) => {
          this.endActivity();
        }
      );*/
    });
  }

  createDatasourceModel(
    studyFile: StudyFile,
    studyFileIds: number[],
    formNeeded: boolean = false
  ): {
    studyFileId: number;
    submittedBy: string;
    date: string;
    imageCount: number;
    stamped: boolean;
    status: number;
    statusMessage: string;
  } {
    let formGroup = null;
    if (formNeeded && !this.authService.IsSalesCompany()) {
      formGroup = new FormGroup({
        clientName: new FormControl('', [Validators.required]),
      });
    } else if (!formNeeded && !this.authService.IsSalesCompany()) {
      formGroup = new FormGroup({
        clientName: new FormControl('', []),
      });
    }
    const model = {
      studyFileId: studyFile.studyFileId,
      submittedBy: studyFile?.createdByUser?.name,
      memberId: studyFile.memberId,
      mimeType: studyFile.mimeType,
      documentTypeName: studyFile.documentTypeName,
      date: studyFile.studyStatusDate,
      imageCount: studyFileIds.length,
      stamped: studyFile.isStamped,
      status: studyFile.studyStatusId,
      statusMessage: studyFile.statusMessage,
      form: formGroup,
      studyFileIds: studyFileIds,
    };

    return model;
  }

  private getSaleMemberAccessData(): void {
    this.saleFacade.loadSaleMemberAccess(this.study.saleId, this.study.horseId);
  }
}
